import React, { useState } from 'react';
import { 
  Card, 
  Upload, 
  Button, 
  Table, 
  Progress, 
  Alert, 
  Space, 
  Typography, 
  Tag, 
  Modal,
  Steps,
  message,
  Row,
  Col,
  Statistic,
  Select
} from 'antd';
import { 
  UploadOutlined, 
  FileExcelOutlined, 
  CheckCircleOutlined, 
  ExclamationCircleOutlined,
  InfoCircleOutlined
} from '@ant-design/icons';
import { useCognitoAuth } from '../contexts/CognitoAuthContext';
import { useApp } from '../contexts/AppContext';
import apiService from '../services/api';

const { Title, Text } = Typography;
const { Option } = Select;

interface ImportResult {
  total: number;
  imported: number;
  duplicates: number;
  errors: number;
  duplicates_list: string[];
  errors_list: string[];
}

interface PreviewData {
  key: string;
  name: string;
  email: string;
  phone: string;
  business_area: string;
  dif: string;
  status: 'valid' | 'error';
}

const Import: React.FC = () => {
  const { user } = useCognitoAuth();
  const { currentBusinessArea } = useApp();
  const [currentStep, setCurrentStep] = useState(0);
  const [fileList, setFileList] = useState<any[]>([]);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState<ImportResult | null>(null);
  const [showResults, setShowResults] = useState(false);
  const [previewData, setPreviewData] = useState<PreviewData[]>([]);
  const [importType, setImportType] = useState<'telecommunications' | 'energy_solutions'>('telecommunications');
  const [leadCategory, setLeadCategory] = useState<number>(0);

  const previewColumns = [
    {
      title: 'Nome',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
      render: (email: string, record: PreviewData) => (
        <span style={{ color: record.status === 'error' ? '#ff4d4f' : 'inherit' }}>
          {email}
        </span>
      ),
    },
    {
      title: 'Telefone',
      dataIndex: 'phone',
      key: 'phone',
    },
    {
      title: 'Área',
      dataIndex: 'business_area',
      key: 'business_area',
      render: (area: string) => (
        <Tag color={area === 'telecommunications' ? 'blue' : 'green'}>
          {area === 'telecommunications' ? 'Telecomunicações' : 'Energia'}
        </Tag>
      ),
    },
    {
      title: 'DIF',
      dataIndex: 'dif',
      key: 'dif',
    },
    {
      title: 'Estado',
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => (
        <Tag color={status === 'valid' ? 'green' : 'red'}>
          {status === 'valid' ? 'Válido' : 'Erro'}
        </Tag>
      ),
    },
  ];

  const resultColumns = [
    {
      title: 'DIF',
      dataIndex: 'dif',
      key: 'dif',
    },
    {
      title: 'Motivo',
      dataIndex: 'reason',
      key: 'reason',
    },
  ];

  const handleUpload = async (info: any) => {
    setFileList(info.fileList);
    
    if (info.file.status === 'done') {
      message.success(`${info.file.name} carregado com sucesso!`);
      
      // Process file for preview
      try {
        const formData = new FormData();
        formData.append('file', info.file.originFileObj);
        formData.append('import_type', importType);
        formData.append('lead_category', leadCategory.toString());
        
        const response = await apiService.importTelecomLeads(info.file.originFileObj, importType, leadCategory);
        setPreviewData(response.preview_data || []);
        setCurrentStep(1);
      } catch (error) {
        message.error('Erro ao processar ficheiro para pré-visualização.');
      }
    } else if (info.file.status === 'error') {
      message.error(`${info.file.name} falha no carregamento.`);
    }
  };

  const handleImport = async () => {
    if (!user || fileList.length === 0) return;
    
    setImporting(true);
    setCurrentStep(2);
    
    try {
      const file = fileList[0].originFileObj;
      let response;
      
      if (importType === 'telecommunications') {
        response = await apiService.importTelecomLeads(file, importType, leadCategory);
      } else {
        response = await apiService.importEnergyLeads(file, leadCategory);
      }
      
      setImportResult(response);
      setImporting(false);
      setCurrentStep(3);
      message.success('Importação concluída!');
    } catch (error) {
      message.error('Erro durante a importação.');
      setImporting(false);
      setCurrentStep(1);
    }
  };

  const uploadProps = {
    name: 'file',
    fileList,
    onChange: handleUpload,
    accept: '.xlsx,.xls',
    beforeUpload: (file: File) => {
      const isExcel = file.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' || 
                     file.type === 'application/vnd.ms-excel';
      if (!isExcel) {
        message.error('Só pode carregar ficheiros Excel!');
        return false;
      }
      const isLt10M = file.size / 1024 / 1024 < 10;
      if (!isLt10M) {
        message.error('O ficheiro deve ser menor que 10MB!');
        return false;
      }
      return false; // Prevent automatic upload, handle manually
    },
  };

  const steps = [
    {
      title: 'Carregar Ficheiro',
      description: 'Selecione o ficheiro Excel',
      icon: <UploadOutlined />,
    },
    {
      title: 'Pré-visualizar',
      description: 'Verificar dados',
      icon: <FileExcelOutlined />,
    },
    {
      title: 'Importar',
      description: 'Processar dados',
      icon: <InfoCircleOutlined />,
    },
    {
      title: 'Concluído',
      description: 'Importação finalizada',
      icon: <CheckCircleOutlined />,
    },
  ];

  if (!user) {
    return null;
  }

  return (
    <div>
      <Title level={2}>Importar Leads</Title>
      
      <Card style={{ marginBottom: 24 }}>
        <Steps current={currentStep} items={steps} />
      </Card>

      {currentStep === 0 && (
        <Card title="Passo 1: Carregar Ficheiro Excel">
          <div style={{ marginBottom: 16 }}>
            <Text strong>Tipo de Importação:</Text>
            <Select
              value={importType}
              onChange={setImportType}
              style={{ width: 200, marginLeft: 8 }}
            >
              <Option value="telecommunications">Telecomunicações</Option>
              <Option value="energy_solutions">Energia</Option>
            </Select>
          </div>
          
          <div style={{ marginBottom: 16 }}>
            <Text strong>Categoria Inicial dos Leads:</Text>
            <Select
              value={leadCategory}
              onChange={setLeadCategory}
              style={{ width: 200, marginLeft: 8 }}
            >
              <Option value={0}>Branco</Option>
              <Option value={2}>Azul</Option>
              <Option value={3}>Amarelo</Option>
              <Option value={4}>Vermelho</Option>
            </Select>
          </div>
          
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <Upload.Dragger {...uploadProps}>
              <p className="ant-upload-drag-icon">
                <FileExcelOutlined style={{ fontSize: '48px', color: '#1890ff' }} />
              </p>
              <p className="ant-upload-text">Clique ou arraste um ficheiro Excel para esta área</p>
              <p className="ant-upload-hint">
                Suporta ficheiros .xlsx e .xls até 10MB
              </p>
            </Upload.Dragger>
          </div>
          
          <Alert
            message="Formato Esperado"
            description={
              importType === 'telecommunications' 
                ? "O ficheiro Excel deve conter as seguintes colunas: Nome, Email, Telefone, DIF"
                : "O ficheiro Excel deve conter as seguintes colunas: Nome, Email, Telefone, DIF"
            }
            type="info"
            showIcon
            style={{ marginTop: 16 }}
          />
        </Card>
      )}

      {currentStep === 1 && (
        <Card 
          title="Passo 2: Pré-visualizar Dados"
          extra={
            <Space>
              <Button onClick={() => setCurrentStep(0)}>
                Voltar
              </Button>
              <Button type="primary" onClick={handleImport}>
                Iniciar Importação
              </Button>
            </Space>
          }
        >
          <div style={{ marginBottom: 16 }}>
            <Row gutter={16}>
              <Col span={6}>
                <Statistic title="Total de Linhas" value={previewData.length} />
              </Col>
              <Col span={6}>
                <Statistic 
                  title="Válidas" 
                  value={previewData.filter(d => d.status === 'valid').length} 
                  valueStyle={{ color: '#3f8600' }} 
                />
              </Col>
              <Col span={6}>
                <Statistic 
                  title="Com Erros" 
                  value={previewData.filter(d => d.status === 'error').length} 
                  valueStyle={{ color: '#cf1322' }} 
                />
              </Col>
              <Col span={6}>
                <Statistic 
                  title="Taxa de Sucesso" 
                  value={previewData.length > 0 ? Math.round((previewData.filter(d => d.status === 'valid').length / previewData.length) * 100) : 0} 
                  suffix="%" 
                />
              </Col>
            </Row>
          </div>
          
          <Table
            columns={previewColumns}
            dataSource={previewData}
            pagination={{ pageSize: 5 }}
          />
        </Card>
      )}

      {currentStep === 2 && (
        <Card title="Passo 3: Importando Dados">
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <Progress type="circle" percent={importing ? 75 : 100} />
            <div style={{ marginTop: 16 }}>
              <Text>
                {importing ? 'A processar dados...' : 'Importação concluída!'}
              </Text>
            </div>
          </div>
        </Card>
      )}

      {currentStep === 3 && importResult && (
        <Card 
          title="Passo 4: Resultados da Importação"
          extra={
            <Button type="primary" onClick={() => setShowResults(true)}>
              Ver Detalhes
            </Button>
          }
        >
          <Row gutter={16} style={{ marginBottom: 24 }}>
            <Col span={6}>
              <Statistic 
                title="Total Processado" 
                value={importResult.total} 
                prefix={<FileExcelOutlined />}
              />
            </Col>
            <Col span={6}>
              <Statistic 
                title="Importados" 
                value={importResult.imported} 
                valueStyle={{ color: '#3f8600' }}
                prefix={<CheckCircleOutlined />}
              />
            </Col>
            <Col span={6}>
              <Statistic 
                title="Duplicados" 
                value={importResult.duplicates} 
                valueStyle={{ color: '#fa8c16' }}
                prefix={<ExclamationCircleOutlined />}
              />
            </Col>
            <Col span={6}>
              <Statistic 
                title="Erros" 
                value={importResult.errors} 
                valueStyle={{ color: '#cf1322' }}
                prefix={<ExclamationCircleOutlined />}
              />
            </Col>
          </Row>

          <Alert
            message="Importação Concluída"
            description={`${importResult.imported} leads foram importados com sucesso. ${importResult.duplicates} duplicados e ${importResult.errors} erros encontrados.`}
            type="success"
            showIcon
          />
        </Card>
      )}

      <Modal
        title="Detalhes da Importação"
        open={showResults}
        onCancel={() => setShowResults(false)}
        footer={[
          <Button key="close" onClick={() => setShowResults(false)}>
            Fechar
          </Button>
        ]}
        width={800}
      >
        {importResult && (
          <div>
            <Card title="Leads Duplicados" style={{ marginBottom: 16 }}>
              <Table
                columns={resultColumns}
                dataSource={importResult.duplicates_list.map((dif, index) => ({
                  key: index,
                  dif,
                  reason: 'DIF já existe no sistema',
                }))}
                pagination={false}
              />
            </Card>
            
            <Card title="Erros de Validação">
              <Table
                columns={resultColumns}
                dataSource={importResult.errors_list.map((dif, index) => ({
                  key: index,
                  dif,
                  reason: 'Formato de DIF inválido',
                }))}
                pagination={false}
              />
            </Card>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default Import; 